import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";
import { Helmet } from 'react-helmet';

import {
    Avatar,
    Box,
    Card,
    Grid,
    IconButton,
    ListItem,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import { AddCircleOutlined } from '@mui/icons-material';

import TrashIcon from '../../icons/TrashIcon';

import apiClient from '../../../services/api';

import store from '../../../store';
import { toggleMessageOpen } from '../../../store/actions/Message';
import { createConfirmAction } from '../../../store/actions/ConfirmDialog';

import { LoadingScreen } from '../../partials/LoadingScreen';

import StyledTextField from '../../styled/TextField';
import StyledButton from '../../styled/Button';

const UserSearch = ({ t, searchUsers, filterUsers, clearSelected }) => {
    return (
        <Fragment>
            <label htmlFor="search" style={{ display: 'block', marginTop: '10px' }}>{t('Search user')}</label>
            <Grid container spacing={2} alignContent="center" mb={2}>
                <Grid item xs={9}>
                    <StyledTextField
                        value={searchUsers}
                        onChange={filterUsers}
                        id="search"
                        size="small"
                        fullWidth
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={3}>
                    <StyledButton
                        fullWidth
                        variant="contained"
                        startIcon={<AddCircleOutlined />}
                        onClick={clearSelected}
                    >
                        {t('Add user')}
                    </StyledButton>
                </Grid>
            </Grid>
        </Fragment>
    )
}

const UserListContainer = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const UserList = ({filteredUsers, t}) => {
        return (
            filteredUsers && filteredUsers.length > 0 ? (
                <AutoSizer style={{ width: '100%' }} disableWidth>
                    {({ height }) => (
                        <FixedSizeList height={height - 140} itemSize={96} itemCount={filteredUsers.length} overscanCount={8} style={{ overflow: 'auto', textOverflow: 'ellipsis'}}>
                            {props.renderUserRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            ) : (
                <Typography style={{padding: '1em', display: (filteredUsers.length === 0 ? 'block' : 'none'), color: theme.palette.storeDrawer.tabs.label.color}}>
                    {t('No users found!')}
                </Typography>
            )
        )
    }

    const UsersDelete = ({handleSelectAll, allSelected, handleDelete}) => {
        return (
            <Grid container alignItems='center' alignContent='center' style={{marginBottom: '5px'}}>
                <Grid
                    item
                    xs={1}
                    style={{
                        display: 'flex',
                        justifyContent: 'start',
                        backgroundColor: theme.palette.background.tab,
                        paddingTop: '2px',
                        paddingBottom: '2px',
                        borderRadius: '8px'
                    }}
                >
                    <input
                        type='checkbox'
                        checked={allSelected}
                        style={{marginRight: '5px', marginLeft: '15px'}}
                        onChange={handleSelectAll}
                    />
                    <IconButton 
                        onClick={() => {
                            store.dispatch(createConfirmAction(handleDelete, t('Are you sure?'), 'selected'))
                        }}
                    >
                        <TrashIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={11}></Grid>
            </Grid>
        )
    }

    return (
        <Paper sx={{ padding: 2, height: '100%' }}>
            <Typography variant='h1'>{t('Users')}</Typography>
            <UserSearch
                t={t}
                searchUsers={props.searchUsers}
                filterUsers={props.filterUsers}
                clearSelected={props.clearSelected}
            />
            <UsersDelete
                handleSelectAll={props.handleSelectAll}
                allSelected={props.allSelected}
                handleDelete={props.handleDelete}
            />
            <UserList
                filteredUsers={props.filteredUsers}
                t={t}
            />
        </Paper>
    )
}

const UserForm = (props) => {
    return (
        <Paper sx={{ padding: 2 }}>
            <Typography variant='h2' style={{marginBottom: '10px'}}>{props.t('Edit user information')}</Typography>
            <StyledTextField
                size="small"
                margin="dense"
                id="name"
                label={props.t("Name")}
                type="text"
                fullWidth
                variant="outlined"
                value={props.name}
                onChange={props.handleChangeName}
                error={props.errors.name}
                helperText={props.errors.name}
            />
            <StyledTextField
                size="small"
                margin="dense"
                id="email"
                label={props.t("Email")}
                type="email"
                fullWidth
                variant="outlined"
                value={props.email}
                onChange={props.handleChangeEmail}
                error={props.errors.email}
                helperText={props.errors.email}
            />
            <StyledTextField
                size="small"
                margin="dense"
                id="password"
                label={props.t("Password")}
                type="password"
                fullWidth
                variant="outlined"
                value={props.password}
                onChange={props.handleChangePassword}
                error={props.errors.password}
                helperText={props.errors.password}
            />
            <StyledButton
                variant="contained"
                sx={{ mt: 2 }}
                onClick={props.handleSave}
            >
                {props.selectedUser ? props.t('Update') : props.t('Add')}
            </StyledButton>
        </Paper>
    )
}

function Users() {
    const {t} = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate()

    const [selectedUser, setSelectedUser] = useState(null);
    const [allSelected, setAllSelected] = useState(false);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchUsers, setSearchUsers] = useState('');
    const [errors, setErrors] = useState([]);

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChangeName = (event) => { setName(event.target.value) }
    const handleChangeEmail = (event) => { setEmail(event.target.value) }
    const handleChangePassword = (event) => { setPassword(event.target.value) }

    useEffect(() => {
        setLoading(true);
        
        apiClient.post('/api/modules/check', {name: 'users'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        apiClient.get('/api/user').then(response => {
            response.data.map(function(item) { item.selected = false; return item})

            setUsers(response.data);
            setFilteredUsers( response.data )
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t("Users can't be loaded"), "error"));
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(selectedUser) {
            setName(selectedUser.name)
            setEmail(selectedUser.email)
            setPassword('')
        }
    }, [selectedUser])

    const getUserData = () => {
        return {
            id: selectedUser ? selectedUser.id : null,
            name: name,
            email: email,
            password: password
        }
    }

    const handleDelete = (type, item) => {
        let ids;

        if(type === 'selected') {
            let selected = [];

            filteredUsers.map(function(user, index) {
                if(filteredUsers[index]['selected']) {
                    selected.push(user.id);
                }

                return false;
            });

            ids = selected.join(',');
        } else {
            ids = item.id;
        }

        apiClient.post('/api/users/delete', {ids: ids}).then(response => {
            if(response.data.success) {
                setUsers(response.data.users);
                setFilteredUsers(response.data.users);
                
                store.dispatch(toggleMessageOpen(t('User deleted successfully'), "success"));
            }
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")) )
    }
    
    const handleSave = () => {
        apiClient.post('/api/users/save', getUserData()).then(response => {
            if(response.data.success) {
                setUsers(response.data.users);
                setFilteredUsers(response.data.users);
                setErrors([]);

                store.dispatch(toggleMessageOpen(t('User saved successfully'), "success"));
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, "error"));
            }
        }).catch( error => setErrors(error.response.data) );
    }

    const renderUserRow = (rowProps) => {
        const { index, style } = rowProps;
      
        return (
            <ListItem
                style={style}
                sx={{marginBottom: '10px'}}
                key={`user`+index}
                component="div"
                disablePadding
                onClick={(event) => handleUserClicked(event, index)}
            >
                <Card 
                    sx={{
                        width: '100%',
                        backgroundColor: theme.palette.background.tab,
                        border: localStorage.getItem('currentTheme') !== 'Dark'
                            ? '1px solid ' + theme.palette.border.main
                            : 'none'
                    }}
                >
                    <Grid container alignItems='center' justifyItems='center' style={{height: '86px'}}>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'start'}}>
                            <input 
                                type="checkbox"
                                checked={filteredUsers[index]['selected']}
                                onChange={(event) => handelSelectedChange(event, index)}
                                onClick={(event) => {event.stopPropagation()}}
                                style={{marginLeft: '15px'}}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <Avatar variant='rounded' />
                        </Grid>
                        <Grid item xs={9}>
                            <div>{filteredUsers[index]['name']}</div>
                            <div style={{color: theme.palette.text.secondary}}>{filteredUsers[index]['email']}</div>
                        </Grid>
                        <Grid item xs={1} style={{display: 'flex', justifyContent: 'end'}}>
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    store.dispatch(createConfirmAction(handleDelete, t('Are you sure?'), 'single', filteredUsers[index]))
                                }}
                            >
                                <TrashIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Card>
            </ListItem>
        );
    }
    
    const filterUsers = (event) => {
        const keyword = event.target.value;
  
        if(keyword !== '') {
            const results = users.filter(
                user => {
                    const value = user.name + ' ' + user.email;

                    return value.toLowerCase().includes(keyword.toLowerCase())
                }
            );
  
            setFilteredUsers(results)
        } else {
            setFilteredUsers(users)
        }
  
        setSearchUsers(keyword);
    }

    const clearSelected = () => {
        setEmail('');
        setName('');
        setPassword('');

        setSelectedUser(null);
        setErrors([]);
    }

    const handleUserClicked = (event, index) => {
        const updateList = [...filteredUsers];

        updateList.map(function(item, i) {
            if(index !== i) {
                item.selected = false; 
            } else {
                item.selected = true;
            }
            
            return item
        });

        setFilteredUsers(updateList);
        setSelectedUser(filteredUsers[index]);
        setErrors([]);
    }

    const handelSelectedChange = (event, index) => {
        const updateList = [...filteredUsers];
        updateList[index]['selected'] = event.target.checked;

        let nrOfSelected = 0;

        updateList.map(function(item) {
            if(item.selected) {
                nrOfSelected++
            }

            return false;
        })

        if(nrOfSelected === 0 || nrOfSelected > 1) {
            clearSelected()
        }

        setFilteredUsers(updateList);
    }

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        const updateList = [...filteredUsers];

        updateList.map(function(item, i) { item.selected = checked; return item });

        setFilteredUsers(updateList);
        setAllSelected(checked);
    }

        return (
            <Fragment>
                <LoadingScreen display={loading ? true : false} />
                <Fragment>
                    <Helmet><title>{t("Users")}</title></Helmet>
                    <Box padding={3} height="100%" style={{display: loading ? 'none' : 'block'}}>
                        <Grid container spacing={3} sx={{ height: 'calc(100% + 24px)' }}>
                            <Grid item xs={6}>
                                <UserListContainer
                                    filteredUsers={filteredUsers}
                                    searchUsers={searchUsers}
                                    allSelected={allSelected}
                                    filterUsers={filterUsers}
                                    renderUserRow={renderUserRow}
                                    clearSelected={clearSelected}
                                    handleSelectAll={handleSelectAll}
                                    handleDelete={handleDelete}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <UserForm
                                    name={name}
                                    email={email}
                                    password={password}
                                    selectedUser={selectedUser}
                                    errors={errors}
                                    handleChangeName={handleChangeName}
                                    handleChangeEmail={handleChangeEmail}
                                    handleChangePassword={handleChangePassword}
                                    handleSave={handleSave}
                                    t={t}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Fragment>
            </Fragment>
        )
}

export default Users;
